/*
 * cart-recommendations-slider.js (v2)
 *
 * v2 fixes:
 *   1. extractInjectableHtml extracts innerHTML of .cart-recommendations
 *      (not the shopify-section wrapper) so the final DOM matches the
 *      pre-refactor shape exactly: heading + .cart--recommendations--items
 *      as direct children of #cart-recommendations. Prevents CSS regression
 *      on any direct-child selectors.
 *   2. Multi-product merge is source-kind aware. Each section response
 *      carries data-source-kind="recommendations|collection|fallback|empty"
 *      on the .cart-recommendations wrapper. When merging two parallel
 *      /recommendations/products fetches, we prefer "recommendations" kind
 *      responses as the base so a fallback response (one product had no
 *      related recs) can't crowd out a real-recs response from the other
 *      product.
 *   3. Custom collection blending restored. When CUSTOM_COLLECTION_HANDLE
 *      is set and the custom collection returns fewer than CONFIG.LIMIT
 *      cards after server-side filtering, we additionally fetch smart
 *      recommendations and merge them in. Matches prior behavior.
 *
 * Architecture (unchanged from v1):
 *   1. Find #cart-recommendations container(s) (drawer + cart page)
 *   2. Read cart state via /cart.js
 *   3. Choose URL based on cart state + merchant config
 *   4. Extract inner content from section response, inject into containers
 *   5. Re-init BazaarVoice ratings + Product QuickAdd
 *
 * Cart change handling:
 *   theme:cart:updated events trigger re-render. AbortController cancels
 *   in-flight requests when cart state changes mid-fetch.
 */

(function() {
  'use strict';

  // ================================
  // CONFIGURATION
  // ================================
  const SECTION_ID = 'cart-recommendations';
  const CONTAINER_ID = 'cart-recommendations';
  const SECTION_WRAPPER_SELECTOR = `#shopify-section-${SECTION_ID}`;
  const INNER_WRAPPER_SELECTOR = '.cart-recommendations';

  const cfg = (window.cartRecsConfig && window.cartRecsConfig.drawer) || {};
  const CONFIG = {
    LIMIT: 4,
    FETCH_LIMIT: 8,
    FALLBACK_COLLECTION_HANDLE: 'last-chance',
    CUSTOM_COLLECTION_HANDLE: cfg.collectionHandle || null,
    USE_MULTI_PRODUCT_RECS: cfg.useMultiProductRecs !== false,
    DEBOUNCE_DELAY: 150
  };

  // ================================
  // STATE
  // ================================
  const state = {
    cartItemCount: 0,
    cartProductIds: [],
    cartHash: '',
    inFlightController: null,
    initialized: false
  };

  // ================================
  // UTILITIES
  // ================================
  const utils = {
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
      };
    },

    routeUrl(path) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const normalizedRoot = root.endsWith('/') ? root : root + '/';
      const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
      return normalizedRoot + normalizedPath;
    },

    hashCart(items) {
      return JSON.stringify((items || []).map(i => `${i.id}:${i.quantity}`).sort());
    },

    setsEqual(a, b) {
      if (a.length !== b.length) return false;
      const setB = new Set(b);
      for (const x of a) if (!setB.has(x)) return false;
      return true;
    },

    findContainers() {
      return Array.from(document.querySelectorAll(`#${CONTAINER_ID}`));
    },

    updateEmptyCartDisplay(itemCount) {
      const elements = {
        noItems: document.querySelector('.cart--no-items'),
        continueTop: document.querySelector('.cart--continue-shopping-top'),
        continueBottom: document.querySelector('.cart--continue-shopping-bottom'),
        notes: document.querySelector('.cart--notes'),
        shipping: document.querySelector('.cart--shipping')
      };
      const isEmpty = itemCount === 0;
      if (elements.noItems) elements.noItems.style.setProperty('display', isEmpty ? 'block' : 'none', 'important');
      if (elements.continueTop) elements.continueTop.style.setProperty('display', isEmpty ? 'none' : 'block', 'important');
      if (elements.continueBottom) elements.continueBottom.style.setProperty('display', isEmpty ? 'none' : 'block', 'important');
      if (elements.notes) elements.notes.style.setProperty('display', isEmpty ? 'none' : 'block', 'important');
      if (elements.shipping) {
        if (isEmpty) {
          elements.shipping.style.setProperty('display', 'none', 'important');
        } else {
          elements.shipping.style.removeProperty('display');
        }
      }
    }
  };

  // ================================
  // CART STATE
  // ================================
  async function readCart(signal) {
    try {
      const res = await fetch(utils.routeUrl('cart.js'), { credentials: 'same-origin', signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      state.cartItemCount = data.item_count != null ? data.item_count : (data.items ? data.items.length : 0);
      state.cartProductIds = (data.items || []).map(i => i.product_id);
      state.cartHash = utils.hashCart(data.items);
      return data;
    } catch (err) {
      if (err.name === 'AbortError') throw err;
      console.error('[CartRecs] readCart failed:', err);
      return { items: [] };
    }
  }

  // ================================
  // SECTION FETCHING
  // ================================

  async function fetchSectionHtml(url, signal) {
    const res = await fetch(url, { credentials: 'same-origin', signal });
    if (!res.ok) throw new Error(`Section fetch ${res.status}: ${url}`);
    return await res.text();
  }

  function fetchRecommendations(productId, limit, signal) {
    const url = utils.routeUrl(
      `recommendations/products?section_id=${SECTION_ID}&product_id=${encodeURIComponent(productId)}&limit=${limit}&intent=related`
    );
    return fetchSectionHtml(url, signal);
  }

  function fetchCollection(handle, signal) {
    const url = utils.routeUrl(`collections/${encodeURIComponent(handle)}?section_id=${SECTION_ID}`);
    return fetchSectionHtml(url, signal);
  }

  // ================================
  // RESPONSE PARSING
  // ================================

  /**
   * Parse a raw section response into a structured object.
   * Returns { root, sourceKind, cards } where:
   *   - root: HTMLElement, the .cart-recommendations element (or null if missing)
   *   - sourceKind: 'recommendations' | 'collection' | 'fallback' | 'empty' | 'unknown'
   *   - cards: HTMLElement[], the .product-card--root elements within
   */
  function parseSectionResponse(rawHtml) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(rawHtml, 'text/html');
    const root = doc.querySelector(INNER_WRAPPER_SELECTOR);
    if (!root) {
      return { root: null, sourceKind: 'unknown', cards: [] };
    }
    const sourceKind = root.dataset.sourceKind || 'unknown';
    const cards = Array.from(root.querySelectorAll('.product-card--root'));
    return { root, sourceKind, cards };
  }

  /**
   * Extract the innerHTML of the .cart-recommendations element from a raw
   * response. This is what gets injected into #cart-recommendations.
   *
   * The shape is intentional: it unwraps BOTH the shopify-section wrapper
   * AND the .cart-recommendations wrapper, leaving just the heading + items
   * container as direct children of #cart-recommendations — matching the
   * pre-refactor DOM exactly so existing CSS continues to apply.
   */
  function extractInjectableHtml(rawHtml) {
    const { root } = parseSectionResponse(rawHtml);
    if (root) return root.innerHTML;
    // Fallback: section wrapper exists but no inner .cart-recommendations
    // (shouldn't happen with our section, but be defensive).
    const parser = new DOMParser();
    const doc = parser.parseFromString(rawHtml, 'text/html');
    const wrapper = doc.querySelector(SECTION_WRAPPER_SELECTOR);
    return wrapper ? wrapper.innerHTML : '';
  }

  // ================================
  // MERGE LOGIC
  // ================================

  /**
   * Merge supplementary cards into a base parsed response.
   * - Dedupes by data-product-id (base cards win)
   * - Slices final result to CONFIG.LIMIT
   * Mutates the base.root DOM in place. Returns the base unchanged.
   */
  function mergeIntoBase(base, supplementCards) {
    if (!base.root) return base;
    const itemsContainer = base.root.querySelector('.cart--recommendations--items');
    if (!itemsContainer) return base;

    const seen = new Set();
    Array.from(itemsContainer.querySelectorAll('.product-card--root')).forEach(card => {
      if (card.dataset.productId) seen.add(card.dataset.productId);
    });

    for (const card of supplementCards) {
      const id = card.dataset.productId;
      if (id && !seen.has(id)) {
        // Remove the empty-state placeholder if it was the only child.
        const emptyState = itemsContainer.querySelector('.cart-recs--empty');
        if (emptyState) emptyState.remove();
        itemsContainer.appendChild(card.cloneNode(true));
        seen.add(id);
      }
    }

    Array.from(itemsContainer.querySelectorAll('.product-card--root'))
      .slice(CONFIG.LIMIT)
      .forEach(card => card.remove());

    return base;
  }

  /**
   * Convert a parsed (and possibly merged) response back into a raw HTML
   * string compatible with extractInjectableHtml. Used by mergers to return
   * the same shape callers expect.
   */
  function serializeParsed(parsed) {
    if (!parsed || !parsed.root) return '';
    return `<div id="shopify-section-${SECTION_ID}" class="shopify-section">${parsed.root.outerHTML}</div>`;
  }

  /**
   * Multi-product: parallel /recommendations/products fetches, then merge
   * cards from real-recs responses only. A response whose source-kind is
   * 'fallback' is NOT used as the merge base when another response has
   * actual recommendations — this prevents fallback cards from crowding
   * out real related products.
   */
  async function fetchAndMergeMultiProduct(productIds, signal) {
    const responses = await Promise.all(
      productIds.map(id => fetchRecommendations(id, CONFIG.FETCH_LIMIT, signal))
    );
    const parsed = responses.map(parseSectionResponse);

    // Pick the best base. Priority is by source kind, but ALSO requires
    // the response to actually have cards. Edge case (GPT review):
    //   Product A returns kind=recommendations but all cards are filtered
    //   out (in cart, excluded title, etc.) — cards.length === 0
    //   Product B falls back to last-chance with real cards.
    // Without the cards.length check, A wins by kind but renders nothing,
    // and B's usable cards get skipped because they're "just fallback".
    // The cards.length gate prevents that empty-kind-wins failure mode.
    const priority = ['recommendations', 'collection', 'fallback'];
    let base = null;
    for (const kind of priority) {
      base = parsed.find(p => p.sourceKind === kind && p.root && p.cards.length > 0) || null;
      if (base) break;
    }
    if (!base) base = parsed.find(p => p.root) || parsed[0];
    if (!base || !base.root) return responses[0];

    // Only restrict supplements to other 'recommendations' responses when
    // the base ITSELF is real-recs-with-cards. Otherwise (base is collection
    // or fallback), allow any supplement to contribute cards so we don't
    // ship a half-empty carousel.
    const baseIsRecsWithCards = base.sourceKind === 'recommendations' && base.cards.length > 0;
    const supplements = parsed.filter(p => p !== base && p.root);
    const supplementCards = [];
    for (const s of supplements) {
      if (baseIsRecsWithCards && s.sourceKind !== 'recommendations') continue;
      supplementCards.push(...s.cards);
    }

    mergeIntoBase(base, supplementCards);
    return serializeParsed(base);
  }

  // ================================
  // URL CHOICE LOGIC
  // ================================

  /**
   * Decide which fetch strategy to use, returning a Promise<string> of raw
   * section HTML. Custom collection blending is implemented here: if a
   * custom collection is set and yields fewer than CONFIG.LIMIT cards, smart
   * recommendations are additionally fetched and merged in.
   */
  async function loadSectionHtml(cart, signal) {
    const items = cart.items || [];

    // Empty cart: just the fallback collection. No blending in original behavior.
    if (items.length === 0) {
      return await fetchCollection(CONFIG.FALLBACK_COLLECTION_HANDLE, signal);
    }

    const productIds = items.map(i => i.product_id).filter(Boolean);

    // Custom collection set + cart has items: try custom, blend with smart
    // recs if undersupplied. This matches the prior JS behavior:
    //   if collectionProducts.length > 0 → processProducts(collection)
    //   if filtered count < LIMIT → also call loadSmartRecommendations(cart)
    if (CONFIG.CUSTOM_COLLECTION_HANDLE) {
      const customHtml = await fetchCollection(CONFIG.CUSTOM_COLLECTION_HANDLE, signal);
      const customParsed = parseSectionResponse(customHtml);
      const customCardCount = customParsed.cards.length;

      if (customCardCount >= CONFIG.LIMIT || productIds.length === 0) {
        return customHtml;
      }

      // Undersupplied — fetch smart recs and merge.
      try {
        let smartHtml;
        if (CONFIG.USE_MULTI_PRODUCT_RECS && productIds.length > 1) {
          smartHtml = await fetchAndMergeMultiProduct(productIds.slice(0, 2), signal);
        } else {
          smartHtml = await fetchRecommendations(productIds[0], CONFIG.FETCH_LIMIT, signal);
        }
        const smartParsed = parseSectionResponse(smartHtml);
        mergeIntoBase(customParsed, smartParsed.cards);
        return serializeParsed(customParsed);
      } catch (err) {
        if (err.name === 'AbortError') throw err;
        console.warn('[CartRecs] Smart recs supplement failed, returning custom alone:', err);
        return customHtml;
      }
    }

    // No custom collection: smart recs only (single or multi).
    if (productIds.length === 0) {
      return await fetchCollection(CONFIG.FALLBACK_COLLECTION_HANDLE, signal);
    }

    if (CONFIG.USE_MULTI_PRODUCT_RECS && productIds.length > 1) {
      return await fetchAndMergeMultiProduct(productIds.slice(0, 2), signal);
    }
    return await fetchRecommendations(productIds[0], CONFIG.LIMIT, signal);
  }

  // ================================
  // INJECTION + INTEGRATIONS
  // ================================

  function injectIntoContainers(html) {
    const inner = extractInjectableHtml(html);
    const containers = utils.findContainers();
    for (const container of containers) {
      container.innerHTML = inner;
    }
    return containers;
  }

  function initIntegrations(containers) {
    if (window.ProductQuickAdd && typeof window.ProductQuickAdd.init === 'function') {
      window.ProductQuickAdd.init();
    }

    if (window.$BV && typeof window.$BV.ui === 'function') {
      const productIds = new Set();
      for (const container of containers) {
        container.querySelectorAll('.product-card--root').forEach(card => {
          const id = card.dataset.productId;
          if (id) productIds.add(id);
        });
      }
      if (productIds.size > 0) {
        try {
          window.$BV.ui('rr', 'inline_ratings', { productIds: Array.from(productIds) });
        } catch (err) {
          console.warn('[CartRecs] BV inline_ratings init failed:', err);
        }
      }
    }
  }

  // ================================
  // MAIN PIPELINE
  // ================================

  async function refresh() {
    if (state.inFlightController) {
      state.inFlightController.abort();
    }
    const controller = new AbortController();
    state.inFlightController = controller;
    const signal = controller.signal;

    try {
      const containers = utils.findContainers();
      if (containers.length === 0) return;

      const cart = await readCart(signal);
      utils.updateEmptyCartDisplay(state.cartItemCount);

      const html = await loadSectionHtml(cart, signal);

      if (signal.aborted) return;

      const updatedContainers = injectIntoContainers(html);
      initIntegrations(updatedContainers);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[CartRecs] refresh failed:', err);
    } finally {
      if (state.inFlightController === controller) {
        state.inFlightController = null;
      }
    }
  }

  const debouncedRefresh = utils.debounce(refresh, CONFIG.DEBOUNCE_DELAY);

  // ================================
  // CART CHANGE HANDLING
  // ================================

  async function onCartMaybeChanged() {
    const oldHash = state.cartHash;
    const oldIds = state.cartProductIds.slice();

    try {
      const res = await fetch(utils.routeUrl('cart.js'), { credentials: 'same-origin' });
      if (!res.ok) return;
      const data = await res.json();
      const newHash = utils.hashCart(data.items);
      const newIds = (data.items || []).map(i => i.product_id);
      const newItemCount = data.item_count != null ? data.item_count : (data.items ? data.items.length : 0);

      const cartChanged = newHash !== oldHash || !utils.setsEqual(oldIds, newIds);

      utils.updateEmptyCartDisplay(newItemCount);

      if (newItemCount > 0 && cartChanged) {
        const scrollTargets = ['.cart--title', '.cart--header', '.cart--body', '.drawer--container[data-view="cart-drawer"]'];
        for (const selector of scrollTargets) {
          const el = document.querySelector(selector);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            break;
          }
        }
      }

      const hasEmptyContainer = utils.findContainers().some(c => c.innerHTML.trim() === '');
      if (cartChanged || hasEmptyContainer) {
        debouncedRefresh();
      }
    } catch (err) {
      console.error('[CartRecs] onCartMaybeChanged failed:', err);
    }
  }

  // ================================
  // INITIALIZATION
  // ================================

  async function init() {
    if (state.initialized) return;
    if (utils.findContainers().length === 0) return;
    state.initialized = true;
    await refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ================================
  // EVENT BINDINGS
  // ================================
  window.addEventListener('theme:cart:updated', onCartMaybeChanged);
  window.addEventListener('theme:cart:add', onCartMaybeChanged);
  window.addEventListener('theme:cart:quantity', onCartMaybeChanged);
  window.addEventListener('theme:drawer:open', (e) => {
    if (e && e.detail && e.detail.view === 'cart-drawer') {
      setTimeout(init, 100);
      setTimeout(onCartMaybeChanged, 200);
    }
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      onCartMaybeChanged();
    }
  });

  window.addEventListener('pageshow', (e) => {
    if (e.persisted) {
      state.initialized = false;
      init();
    }
  });

})();